import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { styled } from 'styletron-react';
import {
  DocumentIdentifier,
  waitForTermsOfService
} from 'frameworks_drivers/localization/Localization';
import { TechemTheme } from '@techem/techem-theme';
import CenteredLoadingIndicator from 'presentation/components/CenteredLoadingIndicator';
import { Col, Row } from 'react-bootstrap';

const Footer = styled('footer', {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  paddingTop: '16px',
  paddingBottom: '16px'
});

const Center = styled('div', {
  marginTop: '16px',
  textAlign: 'center'
});

const openNewTab = (url: string) => {
  window.open(url, '_blank');
};

const LinkElement = styled('span', {
  cursor: 'pointer',
  color: TechemTheme.colors.primary,
  ':hover': {
    textDecoration: 'underline'
  }
});

const loadingView = (
  <div
    style={{
      margin: 'auto',
      display: 'flex',
      transitionDuration: '350ms',
      top: '0',
      left: '0',
      height: '100%',
      width: '100%'
    }}
  >
    <Row style={{ marginLeft: 'auto', marginRight: 'auto' }} className="my-5">
      <Col xs={12} style={{ display: 'flex' }} className="my-3">
        <span style={{ margin: 'auto' }}>
          <Trans i18nKey="TOSLoadingInfoText" />
        </span>
      </Col>
      <Col xs={12} style={{ minHeight: '300px' }}>
        <CenteredLoadingIndicator />
      </Col>
    </Row>
  </div>
);

export const TOSContent: React.FC = () => {
  const { t } = useTranslation(DocumentIdentifier.TermsOfService);
  const [contentLoaded, setContentLoaded] = React.useState(false);

  // Loop through all links in translation file and create inline clickable link elements for them.
  // Respecting the order from the translation file, we can then leave the correct link indexing to the translation file instead of adding logic here.
  const linkObjects = Array.from({ length: 19 }, (_, i) => i).map((index) => {
    return (
      <LinkElement
        key={index}
        onClick={() => {
          openNewTab(t(`content.links.${index}.linkUrl`));
        }}
        dangerouslySetInnerHTML={{
          __html: t(`content.links.${index}.linkText`)
        }}
      />
    );
  });

  const Paragraph = (props: any) => {
    return (
      <p
        className="copy"
        style={{
          hyphens: 'auto',
          textAlign: props.centered ? 'center' : 'justify'
        }}
      >
        <Trans
          ns={DocumentIdentifier.TermsOfService}
          i18nKey={props.i18nKey}
          tOptions={{ skipPostProcessing: true }}
        >
          {linkObjects}
        </Trans>
      </p>
    );
  };

  const contentView = (
    <>
      <main>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_1.title" />
          </h4>
          <Paragraph i18nKey="content.section_1.paragraph_1" />
          <Paragraph i18nKey="content.section_1.paragraph_2" />
          <Paragraph i18nKey="content.section_1.paragraph_3" />
          <Paragraph i18nKey="content.section_1.paragraph_4" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_2.title" />
          </h4>
          <Paragraph i18nKey="content.section_2.paragraph_1" />
          <Paragraph i18nKey="content.section_2.paragraph_2" />
          <Paragraph i18nKey="content.section_2.paragraph_3" />
          <Paragraph i18nKey="content.section_2.paragraph_4" />
          <Paragraph i18nKey="content.section_2.paragraph_5" />
          <Paragraph i18nKey="content.section_2.paragraph_6" />
          <Paragraph i18nKey="content.section_2.paragraph_7" />
          <Paragraph i18nKey="content.section_2.paragraph_8" />
          <Paragraph i18nKey="content.section_2.paragraph_9" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_3.title" />
          </h4>
          <Paragraph i18nKey="content.section_3.paragraph_1" />
          <Paragraph i18nKey="content.section_3.paragraph_2" />
          <Paragraph i18nKey="content.section_3.paragraph_3" />
          <Paragraph i18nKey="content.section_3.paragraph_4" />
          <Paragraph i18nKey="content.section_3.paragraph_5" />
          <Paragraph i18nKey="content.section_3.paragraph_6" />
          <Paragraph i18nKey="content.section_3.paragraph_7" />
          <Paragraph i18nKey="content.section_3.paragraph_8" />
          <Paragraph i18nKey="content.section_3.paragraph_9" />
          <Paragraph i18nKey="content.section_3.paragraph_10" />
          <Paragraph i18nKey="content.section_3.paragraph_11" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_4.title" />
          </h4>
          <Paragraph i18nKey="content.section_4.paragraph_1" />
          <Paragraph i18nKey="content.section_4.paragraph_2" />
          <Paragraph i18nKey="content.section_4.paragraph_3" />
          <Paragraph i18nKey="content.section_4.paragraph_4" />
          <Paragraph i18nKey="content.section_4.paragraph_5" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_5.title" />
          </h4>
          <Paragraph i18nKey="content.section_5.paragraph_1" />
          <Paragraph i18nKey="content.section_5.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_6.title" />
          </h4>
          <Paragraph i18nKey="content.section_6.paragraph_1" />
          <Paragraph i18nKey="content.section_6.paragraph_2" />
          <Paragraph i18nKey="content.section_6.paragraph_3" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_7.title" />
          </h4>
          <Paragraph i18nKey="content.section_7.paragraph_1" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_8.title" />
          </h4>
          <Paragraph i18nKey="content.section_8.paragraph_1" />
          <Paragraph i18nKey="content.section_8.paragraph_2" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_9.title" />
          </h4>
          <Paragraph i18nKey="content.section_9.paragraph_1" />
          <Paragraph i18nKey="content.section_9.paragraph_2" />
          <Paragraph i18nKey="content.section_9.paragraph_3" />
          <Paragraph i18nKey="content.section_9.paragraph_4" />
        </section>
        <section>
          <h4>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.section_10.title" />
          </h4>
          <Paragraph i18nKey="content.section_10.paragraph_1" />
          <Paragraph i18nKey="content.section_10.paragraph_2" />
          <Paragraph i18nKey="content.section_10.paragraph_3" />
          <Paragraph i18nKey="content.section_10.paragraph_4" />
          <Paragraph i18nKey="content.section_10.paragraph_5" />
        </section>
      </main>
      <Footer>
        <Center>
          <div className="text-bold">
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.footer.name" />
          </div>
          <div>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.footer.address" />
          </div>
          <div>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.footer.postal" />
          </div>
          <div>
            <Trans
              ns={DocumentIdentifier.TermsOfService}
              i18nKey="content.footer.website"
              tOptions={{ skipPostProcessing: true }}
            >
              {linkObjects}
            </Trans>
          </div>
          <div>
            <Trans ns={DocumentIdentifier.TermsOfService} i18nKey="content.footer.status" />
          </div>

          <Paragraph i18nKey="content.footer.disclaimer" centered />
        </Center>
      </Footer>
    </>
  );

  React.useEffect(() => {
    if (!contentLoaded) {
      const fetchData = async () => {
        waitForTermsOfService.then((response: any) => {
          setContentLoaded(true);
        });
      };
      fetchData();
    }
  });

  return !contentLoaded ? loadingView : contentView;
};
